// apps/landing-page/src/components/personalization/InterestChipRow.tsx
'use client';

import { CheckCircle2, LayoutGrid } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { cn } from '@/lib/utils';
import type { SegmentExamType } from '@/lib/api';
import type { SegmentPolicyStatus } from '@/hooks/useSegmentPolicy';

export type { SegmentPolicyStatus };

interface InterestChipRowProps {
  examTypes: SegmentExamType[];
  status: SegmentPolicyStatus;
  value: string[];
  /** Toggle a single exam type slug in/out of the selection. */
  onToggle: (slug: string) => void;
  /** "Semua" chip — clears the whole selection. */
  onClear: () => void;
}

// Presentational chip list for the interest step — data comes from the
// parent's single `GET /v1/catalog/segment-policy` fetch so a Siswa never
// even sees CPNS. Never blocks: loading/error/empty all still let the guest
// pick "Semua" and move on. Multiple chips can be selected simultaneously.
// See docs/personalization-spec.md — UX surface A.
export function InterestChipRow({ examTypes, status, value, onToggle, onClear }: InterestChipRowProps) {
  const t = useTranslations('personalization');

  return (
    <div className="flex flex-wrap gap-2">
      {status === 'loading' &&
        [0, 1, 2].map((i) => <div key={i} className="h-8 w-24 rounded-full bg-slate-100 animate-pulse" />)}

      {status === 'error' && <p className="text-xs text-slate-400 py-1.5">{t('interestLoadError')}</p>}

      {status === 'ready' &&
        examTypes.map((examType) => {
          const selected = value.includes(examType.slug);
          return (
            <button
              key={examType.id}
              type="button"
              onClick={() => onToggle(examType.slug)}
              aria-pressed={selected}
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors cursor-pointer',
                selected
                  ? 'border-teal-600 bg-teal-50 text-teal-700'
                  : 'border-slate-200 bg-white text-slate-600 hover:border-teal-300 hover:text-teal-600',
              )}
            >
              {selected && <CheckCircle2 className="w-3.5 h-3.5" />}
              {examType.name}
            </button>
          );
        })}

      {status !== 'loading' && (
        <button
          type="button"
          onClick={onClear}
          aria-pressed={value.length === 0}
          className={cn(
            'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors cursor-pointer',
            value.length === 0
              ? 'border-slate-400 bg-slate-100 text-slate-700'
              : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300',
          )}
        >
          <LayoutGrid className="w-3.5 h-3.5" />
          {t('interestAll')}
        </button>
      )}
    </div>
  );
}
